/**
 * Phase 1 keyword-picker — typeahead search across every completed keyword
 * run in the current project. Backs the shared `KeywordPicker` component.
 *
 * Deliberately keeps its own React Query key namespace so cache invalidation
 * on other keyword flows (e.g. run detail, tracking) doesn't churn every
 * open picker.
 */
import { useQuery } from '@tanstack/react-query';
import type { KeywordSearchResponse } from '@seo/types';
import { apiGet } from '@/lib/api';

export interface KeywordSearchFilters {
  clusterId?: string;
  intent?: 'informational' | 'navigational' | 'commercial' | 'transactional';
}

/**
 * `q` is passed through raw — the component debounces before calling this.
 * Enabled only when projectId is set and the trimmed query is at least 1 char.
 */
export function useKeywordSearch(
  projectId: string | undefined,
  q: string,
  filters?: KeywordSearchFilters,
) {
  const trimmed = q.trim();
  return useQuery({
    enabled: Boolean(projectId) && trimmed.length >= 1,
    queryKey: [
      'keyword-search',
      projectId ?? null,
      trimmed.toLowerCase(),
      filters?.clusterId ?? null,
      filters?.intent ?? null,
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        q: trimmed,
        limit: '20',
        ...(filters?.clusterId ? { cluster_id: filters.clusterId } : {}),
        ...(filters?.intent ? { intent: filters.intent } : {}),
      });
      return apiGet<KeywordSearchResponse>(
        `/v1/projects/${projectId}/keywords/search?${params}`,
      );
    },
    staleTime: 30_000,
  });
}
